import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type User = {
    id: string;
    email: string;
    role: string;
};

type AuthContextType = {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Check for existing session on component mount
    useEffect(() => {
        checkSession();
    }, []);

    const checkSession = async () => {
        try {
            setLoading(true);
            console.log('Checking session...');

            const { data: { session } } = await supabase.auth.getSession();
            console.log('Session check result:', session ? 'Found' : 'Not found');

            if (session?.user) {
                console.log('User found:', session.user.email);

                // Get user role from profiles table
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('role')
                    .eq('id', session.user.id)
                    .single()
                    .catch(() => ({ data: null }));

                setUser({
                    id: session.user.id,
                    email: session.user.email || '',
                    role: profile?.role || 'customer'
                });

                console.log('User role:', profile?.role || 'customer');
            } else {
                console.log('No session found');
                setUser(null);
            }
        } catch (error) {
            console.error('Error checking session:', error);
            setUser(null);
        } finally {
            console.log('Session check completed');
            setLoading(false);
        }
    };

    const login = async (email: string, password: string) => {
        try {
            setLoading(true);
            console.log('Attempting login for:', email);

            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                console.error('Login error:', error.message);
                throw error;
            }

            if (data.user) {
                console.log('Login successful for:', data.user.email);

                // Get user role
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('role')
                    .eq('id', data.user.id)
                    .single()

                const userData: User = {
                    id: data.user.id,
                    email: data.user.email || '',
                    role: profile?.role || 'customer'
                };

                setUser(userData);
                console.log('User set in context:', userData);
            }
        } catch (error: any) {
            console.error('Login failed:', error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoading(true);
            await supabase.auth.signOut();
            setUser(null);
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            logout
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};